import { Box, Typography, alpha } from "@mui/material";
/**
 * Block super-node for the schematic page's "Blocks" view.
 *
 * Visual treatment
 * ----------------
 * Compact super-node — three slots:
 *   • Role chip (Power Supply / MCU / Sensor / Comms / Mixed Signal)
 *     in the corner. Same fixed taxonomy `_infer_block_role` emits.
 *   • Label as the headline ("Block 1", "PSU", whatever Louvain
 *     produced).
 *   • Bottom strip: count + IC count + bus chips ("I2C, SPI").
 *
 * Bus chips deliberately sit at the bottom — engineers reading the
 * board "from input to output" parse the headline first, then look
 * for protocol affinity to confirm what the block does. Putting
 * buses on top would steal the role chip's slot for the most
 * common reading pattern.
 *
 * Buses cap at 4 chips; remaining bus count surfaces as "+N" so
 * dense comms blocks don't dominate the layout. The drawer shows
 * the full list.
 */
import { Handle, type NodeProps, Position } from "@xyflow/react";

import type { BlockEntry } from "@models/circuit-ir";

const VISIBLE_BUS_CHIPS = 4;

export interface BlockNodeData {
  block: BlockEntry;
  highlight?: boolean;
  [key: string]: unknown;
}

export function BlockNode({ data }: NodeProps) {
  const nodeData = data as unknown as BlockNodeData;
  const block = nodeData.block;
  if (!block) return null;

  const role = block.role ?? "Mixed";
  const visibleBuses = block.buses.slice(0, VISIBLE_BUS_CHIPS);
  const hiddenBusCount = block.buses.length - visibleBuses.length;

  return (
    <Box
      sx={(t) => ({
        width: "100%",
        height: "100%",
        borderRadius: 1.5,
        border: `1px solid ${alpha(t.palette.border.default, 1)}`,
        bgcolor: alpha(t.palette.surface.raised, 1),
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxShadow: nodeData.highlight
          ? `0 0 0 2px ${alpha(t.palette.accent.main, 0.9)}, 0 0 18px ${alpha(t.palette.accent.main, 0.5)}`
          : undefined,
        transition: "box-shadow 180ms ease-out",
      })}
    >
      <Box
        sx={(t) => ({
          px: 1.25,
          py: 0.75,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          borderBottom: `1px solid ${alpha(t.palette.border.default, 0.5)}`,
        })}
      >
        <Box
          sx={(t) => ({
            fontSize: "0.625rem",
            fontFamily: t.fonts?.mono ?? "monospace",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: roleColor(role, t.palette.primary.main),
            bgcolor: alpha(roleColor(role, t.palette.primary.main), 0.14),
            borderRadius: 0.5,
            px: 0.625,
            py: 0.125,
            flexShrink: 0,
          })}
        >
          {role}
        </Box>
        <Typography
          variant="body2"
          sx={(t) => ({
            fontWeight: 700,
            fontSize: "0.875rem",
            color: t.palette.text.primary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
          })}
          title={block.label}
        >
          {block.label}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          px: 1.25,
          py: 0.75,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          gap: 0.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          <Typography
            variant="caption"
            sx={(t) => ({
              fontFamily: t.fonts?.mono ?? "monospace",
              fontWeight: 700,
              fontSize: "0.875rem",
              color: t.palette.text.primary,
            })}
          >
            {block.count}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontSize: "0.625rem" }}
          >
            components · {block.ic_count} IC
            {block.ic_count === 1 ? "" : "s"}
          </Typography>
        </Box>

        {(visibleBuses.length > 0 || hiddenBusCount > 0) && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.375 }}>
            {visibleBuses.map((bus) => (
              <BusChip key={bus} label={bus} />
            ))}
            {hiddenBusCount > 0 && (
              <BusChip label={`+${hiddenBusCount}`} muted />
            )}
          </Box>
        )}
      </Box>

      <Handle
        type="target"
        position={Position.Left}
        style={{ visibility: "hidden" }}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{ visibility: "hidden" }}
      />
    </Box>
  );
}

function BusChip({ label, muted }: { label: string; muted?: boolean }) {
  return (
    <Box
      sx={(t) => ({
        fontSize: "0.5625rem",
        fontFamily: t.fonts?.mono ?? "monospace",
        color: muted ? t.palette.text.secondary : t.palette.text.primary,
        bgcolor: alpha(t.palette.text.secondary, muted ? 0.06 : 0.12),
        borderRadius: 0.5,
        px: 0.5,
        py: 0.0625,
      })}
    >
      {label}
    </Box>
  );
}

/** Map role taxonomy to a tint color. We don't try to encode a
 *  full color scheme — the chip is small; consistency matters
 *  more than information density. Power → primary (warm), comms
 *  → accent (cool), everything else stays muted. */
function roleColor(role: string, primary: string): string {
  const lower = role.toLowerCase();
  if (lower.includes("power") || lower.includes("psu")) return primary;
  return primary;
}
